import { Money } from '@/components/text';
import type { FlexProps } from '@chakra-ui/react';
import { Flex, Text } from '@chakra-ui/react';
import type { CollabSettlementQuote } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { formatQuoteRate, minorUnitsToMajor } from './collab-settlement-format';

const KEY = 'collabs.tracking.execution.settlement.quote';

interface QuoteLineProps {
  label: string;
  minorUnits: number;
  currency: string;
  emphasis?: boolean;
  testId?: string;
}

const QuoteLine = ({ label, minorUnits, currency, emphasis, testId }: QuoteLineProps) => (
  <Flex align="center" gap="10px" data-testid={testId}>
    <Text
      flex="1"
      minW="0"
      fontSize="12.5px"
      fontWeight={emphasis ? 600 : 400}
      color={emphasis ? 'ink' : 'primary.500'}
    >
      {label}
    </Text>
    <Money
      fontSize="13px"
      fontWeight={emphasis ? 600 : 400}
      color={emphasis ? 'ink' : 'primary.500'}
      value={minorUnitsToMajor(minorUnits)}
      currency={currency}
      textProps={{ flexShrink: 0, fontFamily: 'heading' }}
    />
  </Flex>
);

interface CollabSettlementQuoteLinesProps extends FlexProps {
  quote: CollabSettlementQuote;
  /**
   * Leads with what the creator receives. The drawer card already carries that
   * amount in its own header, the checkout does not.
   */
  showCreatorPrincipal?: boolean;
}

/**
 * One settlement quote, line by line: what the creator receives, what the brand
 * pays on top, and what is charged in the end.
 *
 * Rendered from the quote the read already carries, never from a preparation:
 * pricing writes a settlement row and is an admin move, while every member of
 * the brand may read what a collab costs.
 *
 * The fee and its tax sit on top of what the creator was promised, never inside
 * it. Both rates are named from the quote's own basis points, and the label
 * simply drops the rate when the quote carries none, because a rate divided out
 * of the amounts here would be a guess presented as a fact. The processing line
 * appears only when there is one to pay, so the amounts above the total always
 * add up to it.
 *
 * No fee line and no total on an exempt quote — internal and partner
 * workspaces pay no Fluenceur fee, and a "0,00" line followed by a total
 * repeating the amount just above it is noise. The exemption is read from the
 * quote, which names it, and never worked out from the amounts: a paying
 * workspace whose commission rounds down to nothing has a total that equals
 * the payout and is not exempt at all. An absent field is read as "not
 * exempt", so a quote that predates it shows its lines rather than falling
 * back to the arithmetic this field exists to retire.
 *
 * The surrounding chrome belongs to the caller: the drawer separates these
 * lines with a rule, the checkout boxes them in.
 */
export const CollabSettlementQuoteLines = ({
  quote,
  showCreatorPrincipal = false,
  ...chrome
}: CollabSettlementQuoteLinesProps) => {
  const { t, i18n } = useTranslation();

  const isExempt = quote.feeExempt === true;
  if (isExempt && !showCreatorPrincipal) return null;

  const feeRate = formatQuoteRate(quote.platformFeeRateBasisPoints, i18n.language);
  const taxRate = formatQuoteRate(quote.feeTaxRateBasisPoints, i18n.language);

  return (
    <Flex data-testid="collab-payment-fees" direction="column" gap="8px" {...chrome}>
      {showCreatorPrincipal && (
        <QuoteLine
          label={t(`${KEY}.principal`)}
          minorUnits={quote.creatorPrincipalMinorUnits}
          currency={quote.currency}
        />
      )}

      {!isExempt && (
        <>
          <QuoteLine
            label={
              feeRate ? t(`${KEY}.platformFee`, { rate: feeRate }) : t(`${KEY}.platformFeeNoRate`)
            }
            minorUnits={quote.platformFeeMinorUnits}
            currency={quote.currency}
          />
          <QuoteLine
            label={taxRate ? t(`${KEY}.feeTax`, { rate: taxRate }) : t(`${KEY}.feeTaxNoRate`)}
            minorUnits={quote.feeTaxMinorUnits}
            currency={quote.currency}
          />
          {quote.processingFeeMinorUnits > 0 && (
            <QuoteLine
              label={t(`${KEY}.processingFee`)}
              minorUnits={quote.processingFeeMinorUnits}
              currency={quote.currency}
            />
          )}
          <QuoteLine
            label={t(`${KEY}.total`)}
            minorUnits={quote.brandTotalMinorUnits}
            currency={quote.currency}
            emphasis
            testId="collab-payment-total"
          />
        </>
      )}
    </Flex>
  );
};
